import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import axiosInstance from "../../services/FetchNodeServices";
import { toast } from "react-toastify";
import DownloadProductStock from "../../Components/template/DownloadProductStock";

const ExcelProductStockUploader = () => {
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const CHUNK_SIZE = 5000;

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadResult(null);

    const loadingToast = toast.loading("📤 Reading Excel file...", {
      position: "top-right",
      autoClose: false,
    });

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet);

        toast.dismiss(loadingToast);

        if (jsonData.length === 0) {
          toast.error("❌ Excel file is empty!", { autoClose: 3000 });
          return;
        }

        const requiredFields = ["ISBN13", "stock"];
        const isValid = requiredFields.every((field) =>
          Object.keys(jsonData[0]).includes(field)
        );

        if (!isValid) {
          toast.error("❌ Columns must include: ISBN13 and stock", { autoClose: 3000 });
          return;
        }

        toast.success(`✅ Excel parsed! ${jsonData.length} products ready.`, { autoClose: 2000 });
        setStockData(jsonData);
      } catch (error) {
        toast.dismiss(loadingToast);
        toast.error("❌ Failed to parse Excel file.", { autoClose: 3000 });
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const uploadInChunks = async (data) => {
    const totalChunks = Math.ceil(data.length / CHUNK_SIZE);
    let totalUpdated = 0;
    let successChunks = 0;

    for (let i = 0; i < data.length; i += CHUNK_SIZE) {
      const chunk = data.slice(i, i + CHUNK_SIZE);
      const currentChunk = i / CHUNK_SIZE + 1;
      const percent = Math.round((currentChunk / totalChunks) * 100);

      // Update loading toast with progress
      toast.dismiss();
      toast.loading(
        `⏳ Uploading chunk ${currentChunk}/${totalChunks} (${percent}%) — ${Math.min(i + CHUNK_SIZE, data.length)}/${data.length} products`,
        { position: "top-right", autoClose: false, toastId: "upload-progress" }
      );

      try {
        const res = await axiosInstance.post(
          "/api/v1/product/update-product-stock",
          { products: chunk }
        );
        totalUpdated += res.data.updated || chunk.length;
        successChunks++;
      } catch (error) {
        toast.dismiss("upload-progress");
        toast.error(
          `❌ Failed at chunk ${currentChunk}/${totalChunks}. ${successChunks * CHUNK_SIZE} products were updated before failure. Please re-upload the full file.`,
          { autoClose: 8000 }
        );
        throw error;
      }
    }

    toast.dismiss("upload-progress");
    return totalUpdated;
  };

  const handleSubmit = async () => {
    if (!stockData.length) {
      toast.error("No data to upload. Upload an Excel file first.");
      return;
    }

    try {
      setLoading(true);
      setUploadResult(null);

      const totalUpdated = await uploadInChunks(stockData);

      toast.success(
        `✅ Stock updated! ${totalUpdated} products updated successfully.`,
        { position: "top-right", autoClose: 5000 }
      );

      setUploadResult({
        success: true,
        updated: totalUpdated,
        total: stockData.length,
      });

      setStockData([]);
      if (fileInputRef.current) fileInputRef.current.value = "";

    } catch (error) {
      setUploadResult({ success: false });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-5">
      <div className="card shadow">
        <div className="card-body">
          <div className="p-3 mb-2 text-start">
            <div className="d-flex flex-column align-items-start mb-3">
              <h4 className="card-title mb-4">Upload Product Stock (Excel)</h4>
              <DownloadProductStock />
            </div>

            {/* ℹ️ How it works */}
            <div style={{
              background: "#e8f4fd",
              border: "1px solid #3498db",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "12px"
            }}>
              <p style={{ fontWeight: "bold", color: "#2980b9", marginBottom: "8px" }}>
                ℹ️ How this upload works:
              </p>
              <ul style={{ fontSize: "13px", color: "#555", paddingLeft: "20px", marginBottom: "0" }}>
                <li>🔍 <strong>Matches by:</strong> ISBN13</li>
                <li>✅ <strong>Products IN file</strong> → stock updated to new value</li>
                <li>✅ <strong>Products NOT in file</strong> → stock <strong style={{ color: "green" }}>UNCHANGED</strong></li>
                <li>✅ <strong>stock: 0</strong> is valid — sets product as out of stock</li>
                <li>📝 <strong>Only stock field is changed</strong> — no other data affected</li>
                <li>📦 <strong>Uploads in chunks</strong> of 5000 products per request</li>
              </ul>
            </div>

            {/* ⚠️ Required Fields */}
            <div style={{
              background: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "8px",
              padding: "12px",
            }}>
              <p style={{ fontWeight: "bold", color: "#856404", marginBottom: "8px" }}>
                ⚠️ Required columns in Excel file:
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {["ISBN13", "stock"].map(field => (
                  <span key={field} style={{
                    background: "#ffc107",
                    color: "#333",
                    padding: "3px 10px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "bold"
                  }}>{field}</span>
                ))}
              </div>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleExcelUpload}
            className="form-control mb-3"
          />

          {/* File loaded info */}
          {stockData.length > 0 && (
            <div className="alert alert-info" role="alert">
              📊 <strong>{stockData.length}</strong> product stock entries ready to upload
            </div>
          )}

          {/* Success Result */}
          {uploadResult?.success && (
            <div className="alert alert-success" role="alert">
              🎉 <strong>Upload Successful!</strong><br />
              ✅ <strong>{uploadResult.updated}</strong> out of{" "}
              <strong>{uploadResult.total}</strong> products updated successfully.
            </div>
          )}

          {/* Failed Result */}
          {uploadResult?.success === false && (
            <div className="alert alert-danger" role="alert">
              ❌ <strong>Upload Failed!</strong> Please re-upload the full file.
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading || stockData.length === 0}
            className="btn"
            style={{ backgroundColor: "#6f42c1", color: "white" }}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Uploading...
              </>
            ) : (
              "Submit Stock Updates"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExcelProductStockUploader;
